import { IoChevronForwardCircleOutline } from "react-icons/io5";
import { LuCalendarDays, LuPackageSearch } from "react-icons/lu";
import { CiPillsBottle1 } from "react-icons/ci";
import { VscTypeHierarchy } from "react-icons/vsc";
import { BiReset } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";

import "../../../../styles.css";
import "../../../../slicer-styles.css";
import RadioDropdown from "../../../UI/RadioDropdown/RadioDropdown";
import { RootState } from "../../../../store";
import { forecastReviewTabActions } from "../../../../store/slices/forecast-review-tab-slice";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { FORECAST_REVIEW_TAB_ID } from "../../../../shared/constants";
import CheckboxDropdown from "../../../UI/CheckboxDropdown/CheckboxDropdown";
import { ForecastReviewDropdownModel, ForecastReviewSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/ForecastReviewSlicer.model";
import { getDefaultSlicersForForecastEntryApp } from "../../../../shared/functions";

const ForecastReviewSlicers = () => {
    // States
    const [isSlicerChanged, setIsSlicerChanged] = useState<boolean>(false);
    
    // Redux variables
    const dispatchFn = useDispatch();
    const slicers: ForecastReviewDropdownModel = useSelector((state: RootState) => state.forecastReviewTab.slicers);
    const tempSelectedSlicers: ForecastReviewSelectedSlicerModel = useSelector((state: RootState) => state.forecastReviewTab.tempSelectedSlicers);

    /**
     * Handler for applying selected slicers.
     * @param {Event} event Apply filters button click event.
     * @returns {void}
     */
    const applySelectedSlicers = (event: any): void => {
        const tempSlicers: ForecastReviewSelectedSlicerModel = { ...tempSelectedSlicers };
        
        const selectedSlicers: ForecastReviewSelectedSlicerModel = {
            sku_brands: tempSlicers.sku_brands.includes("All") ? ["All"] : tempSlicers.sku_brands,
            sku_sub_brands: tempSlicers.sku_sub_brands.includes("All") ? ["All"] : tempSlicers.sku_sub_brands,
            sku_volume_sizes: tempSlicers.sku_volume_sizes.includes("All") ? ["All"] : tempSlicers.sku_volume_sizes,
            year: tempSelectedSlicers.year
        };

        dispatchFn(forecastReviewTabActions.setSelectedSlicers(selectedSlicers));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [FORECAST_REVIEW_TAB_ID]: false
        }));
        setIsSlicerChanged(false);
    };

    /**
     * Handler for resetting selected slicers.
     * @param {Event} event Reset filters button click event.
     * @returns {void}
     */
    const resetSelectedSlicers = (event: any): void => {
        const defaultSlicers: ForecastReviewSelectedSlicerModel = getDefaultSlicersForForecastEntryApp(FORECAST_REVIEW_TAB_ID);

        if (
            tempSelectedSlicers.sku_brands.toSorted().toString() !== defaultSlicers.sku_brands.toSorted().toString() || 
            tempSelectedSlicers.sku_sub_brands.toSorted().toString() !== defaultSlicers.sku_sub_brands.toSorted().toString() || 
            tempSelectedSlicers.sku_volume_sizes.toSorted().toString() !== defaultSlicers.sku_volume_sizes.toSorted().toString() || 
            tempSelectedSlicers.year !== defaultSlicers.year
        ) {
            dispatchFn(forecastReviewTabActions.resetSelectedSlicers(null));
            dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                [FORECAST_REVIEW_TAB_ID]: false
            }));
            setIsSlicerChanged(false);   
        }
    };

    /**
     * Event handler for dropdown value change.
     * @param {string} field Slicer field which was updated.
     * @param {string | string[]} value Updated slicer value(s).
     * @returns {void}
     */
    const dropdownItemChangeHandler = (field: string, value: string | string[]): void => {
        if (["sku_brands", "sku_sub_brands", "sku_volume_sizes"].includes(field)) {
            dispatchFn(forecastReviewTabActions.setTempSlicers({
                ...tempSelectedSlicers,
                [field]: value
            }));
        } else {
            dispatchFn(forecastReviewTabActions.setTempSlicers({
                ...tempSelectedSlicers,
                [field]: Number(value)
            }));
        }
        setIsSlicerChanged(true);
    };

    return (
        <>
            <CheckboxDropdown
                id="forecast-review-sku-brand-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Product Brand(s)"
                selectedValues={tempSelectedSlicers.sku_brands}
                items={slicers.sku_brands}
                onItemChanged={(selectedValues: string[]) => dropdownItemChangeHandler("sku_brands", selectedValues)}
            />

            <CheckboxDropdown
                id="forecast-review-sku-sub-brand-dropdown"
                icon={<VscTypeHierarchy size={20} />}
                label="Product Sub-Brand(s)"
                selectedValues={tempSelectedSlicers.sku_sub_brands}
                items={slicers.sku_sub_brands}
                onItemChanged={(selectedValues: string[]) => dropdownItemChangeHandler("sku_sub_brands", selectedValues)}
            />

            <CheckboxDropdown
                id="forecast-review-sku-volume-size-dropdown"
                icon={<CiPillsBottle1 size={20} />}
                label="Product Volume Size(s)"
                selectedValues={tempSelectedSlicers.sku_volume_sizes}
                items={slicers.sku_volume_sizes}
                onItemChanged={(selectedValues: string[]) => dropdownItemChangeHandler("sku_volume_sizes", selectedValues)}
            />

            <RadioDropdown
                id="forecast-review-year-dropdown"
                icon={<LuCalendarDays size={20} />}
                label="Year"
                selectedValue={tempSelectedSlicers.year.toString()}
                items={slicers.years.map(y => ({ name: y.name, value: y.value.toString() }))}
                onItemChanged={(value: any) => dropdownItemChangeHandler("year", value)}
            />

            <div className="d-inline me-1">
                <button id="btn-forecast-review-apply-filter" type="button" className="btn btn-sm text-white" style={{ backgroundColor: "#00833E" }} onClick={applySelectedSlicers} disabled={!isSlicerChanged}>
                    <div className="d-flex justify-content-center align-items-center">
                        <IoChevronForwardCircleOutline size={20} /> &nbsp;Apply filters
                    </div>
                </button>
            </div>

            <div className="d-inline">
                <button id="btn-forecast-review-reset-filter" type="button" className="btn btn-sm btn-warning" onClick={resetSelectedSlicers}>
                    <div className="d-flex justify-content-center align-items-center">
                        <BiReset size={20} /> &nbsp;Reset filters
                    </div>
                </button>
            </div>
        </>
    );
};

export default ForecastReviewSlicers;
